import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel, PanelGroup } from 'react-resizable-panels';

// Custom
import AppPage from '@/ui/AppPage/AppPage';
import StyledPanelResizeHandle from '@/ui/PanelResize/StyledPanelResizeHandle';
import Permissions from '@/lib/permissions';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import ZatvaranjeVentilaMap, { ZatvaranjeVentilaMapMode } from './ZatvaranjeVentilaMap';
import ZatvaranjeVentilaPanel from './ZatvaranjeVentilaPanel';

// Services
import { plinService, Simulacija, SimulacijaRezultat, SimulacijaSave } from '@/services/plinService';

const MAP_ID = 55;

const ZatvaranjeVentilaPage = () => {
  const { t } = useTranslation();
  const { showNotification } = useContext(SnackbarContext);

  const [simulacije, setSimulacije] = useState<Simulacija[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [rezultat, setRezultat] = useState<SimulacijaRezultat | null>(null);
  const [loading, setLoading] = useState(false);
  const [placingNewLeak, setPlacingNewLeak] = useState(false);
  const [focusPotrosac, setFocusPotrosac] = useState<{ id: number; seq: number } | null>(null);

  // valve clicks can outrun the 0.3 s calculation, only the newest answer wins
  const requestSeq = useRef(0);

  const selected = simulacije.find((s) => s.id === selectedId) ?? null;

  const mode: ZatvaranjeVentilaMapMode = placingNewLeak
    ? 'curenje'
    : selected?.tip === 'zatvaranje'
    ? 'ventili'
    : selected?.tip === 'curenje'
    ? 'curenje'
    : 'none';

  const showError = (resp: { error?: any }) => {
    showNotification(resp?.error?.detail?.message || resp?.error?.message || t('plin:error.generic'), 'error');
  };

  const loadSimulacije = () =>
    plinService.getSimulacije().then((resp) => {
      if (resp.success) {
        setSimulacije(Array.isArray(resp.data) ? (resp.data as Simulacija[]) : []);
      } else {
        showError(resp);
      }
      return resp.success;
    });

  const loadRezultat = (id: number) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    plinService.getRezultat(id).then((resp) => {
      if (seq !== requestSeq.current) return;
      setLoading(false);
      if (resp.success) {
        setRezultat(resp.data as SimulacijaRezultat);
      } else {
        setRezultat(null);
        showError(resp);
      }
    });
  };

  useEffect(() => {
    loadSimulacije();
  }, []);

  useEffect(() => {
    setFocusPotrosac(null);
    if (selectedId) {
      loadRezultat(selectedId);
    } else {
      requestSeq.current++;
      setRezultat(null);
      setLoading(false);
    }
  }, [selectedId]);

  const saveSelected = (sim: Simulacija, changes: Partial<SimulacijaSave>) => {
    const data: SimulacijaSave = { naziv: sim.naziv, ventil_ids: sim.ventil_ids, wkt: null, ...changes };
    // optimistic, so fast clicking toggles against the latest valve list
    setSimulacije((prev) => prev.map((s) => (s.id === sim.id ? { ...s, ...changes, wkt: changes.wkt ?? s.wkt } : s)));
    return plinService.updateSimulacija(sim.id, data).then((resp) => {
      // server snaps the leak onto the pipe, reload to draw it there
      if (!resp.success || changes.wkt) {
        if (!resp.success) showError(resp);
        loadSimulacije();
      }
      return resp.success;
    });
  };

  const insert = (data: Parameters<typeof plinService.insertSimulacija>[0]) =>
    plinService.insertSimulacija(data).then((resp) => {
      if (!resp.success) {
        showError(resp);
        return;
      }
      const newId = (resp.data as { id: number }).id;
      loadSimulacije().then(() => setSelectedId(newId));
    });

  const nextName = (base: string) => `${base} ${simulacije.filter((s) => s.naziv.startsWith(base)).length + 1}`;

  const handleNewZatvaranje = () => {
    setPlacingNewLeak(false);
    insert({ naziv: nextName(t('plin:tip.zatvaranje')), tip: 'zatvaranje', ventil_ids: [] });
  };

  const handleNewCurenje = () => {
    setPlacingNewLeak((prev) => !prev);
  };

  const handleValveClick = (ids: number[]) => {
    if (!selected || selected.tip !== 'zatvaranje') return;
    const isClosed = ids.some((id) => selected.ventil_ids.includes(id));
    const ventilIds = isClosed ? selected.ventil_ids.filter((id) => !ids.includes(id)) : [...selected.ventil_ids, ids[0]];
    saveSelected(selected, { ventil_ids: ventilIds }).then((ok) => ok && loadRezultat(selected.id));
  };

  const handleRemoveValves = (ids: number[]) => handleValveClick(ids);

  const handleLeakClick = (wkt: string) => {
    if (placingNewLeak) {
      setPlacingNewLeak(false);
      insert({ naziv: nextName(t('plin:tip.curenje')), tip: 'curenje', wkt });
    } else if (selected?.tip === 'curenje') {
      saveSelected(selected, { wkt }).then((ok) => ok && loadRezultat(selected.id));
    }
  };

  const handleSelect = (id: number) => {
    setPlacingNewLeak(false);
    setSelectedId((prev) => (prev === id ? null : id));
  };

  const handleRename = (sim: Simulacija, naziv: string) => {
    saveSelected(sim, { naziv });
  };

  const handleDelete = (sim: Simulacija) => {
    plinService.deleteSimulacija(sim.id).then((resp) => {
      if (!resp.success) {
        showError(resp);
        return;
      }
      if (sim.id === selectedId) setSelectedId(null);
      setSimulacije((prev) => prev.filter((s) => s.id !== sim.id));
    });
  };

  return (
    <AppPage titlett="plin:page.zatvaranje_ventila" needPermission={[Permissions.PlinSimulacijeVentila]}>
      <PanelGroup direction="horizontal">
        <Panel defaultSize={65} minSize={30}>
          <ZatvaranjeVentilaMap
            mapId={MAP_ID}
            mode={mode}
            rezultat={rezultat && rezultat.id === selectedId ? rezultat : null}
            leakWkt={placingNewLeak ? null : selected?.tip === 'curenje' ? selected.wkt : null}
            onValveClick={handleValveClick}
            onLeakClick={handleLeakClick}
            focusPotrosac={focusPotrosac}
          />
        </Panel>
        <StyledPanelResizeHandle variant="vertical" />
        <Panel defaultSize={35} minSize={20}>
          <ZatvaranjeVentilaPanel
            simulacije={simulacije}
            selected={selected}
            rezultat={rezultat}
            loading={loading}
            placingNewLeak={placingNewLeak}
            onSelect={handleSelect}
            onNewZatvaranje={handleNewZatvaranje}
            onNewCurenje={handleNewCurenje}
            onRename={handleRename}
            onDelete={handleDelete}
            onRemoveValves={handleRemoveValves}
            focusedPotrosacId={focusPotrosac?.id ?? null}
            onPotrosacClick={(id) => setFocusPotrosac((prev) => ({ id, seq: (prev?.seq ?? 0) + 1 }))}
          />
        </Panel>
      </PanelGroup>
    </AppPage>
  );
};

export default ZatvaranjeVentilaPage;
